import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plus,
  Search,
  Sparkles,
  Calendar,
  BookOpen,
  Trash2,
  Loader2,
  FolderOpen,
  Library
} from "lucide-react";
import { ModeToggle } from "@/components/mode-toggle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function LibraryPage() {
  const navigate = useNavigate();
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  // Fetch saved topics
  useEffect(() => {
    const fetchTopics = async () => {
      try {
        const response = await fetch(`${API}/topics?saved_only=true`);
        if (!response.ok) throw new Error("Failed to fetch");
        const data = await response.json();
        setTopics(data);
      } catch (error) {
        toast.error("Failed to load library");
      } finally {
        setLoading(false);
      }
    };

    fetchTopics();
  }, []);

  // Filter topics by search
  const filteredTopics = topics.filter((topic) => {
    const query = searchQuery.toLowerCase();
    return (
      topic.summary?.toLowerCase().includes(query) ||
      topic.input_text?.toLowerCase().includes(query) ||
      topic.classifier?.tags?.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  // Delete topic
  const handleDelete = async (topicId) => {
    setDeletingId(topicId);
    try {
      await fetch(`${API}/topics/${topicId}`, { method: "DELETE" });
      setTopics((prev) => prev.filter((t) => t.id !== topicId));
      toast.success("Topic removed from library");
    } catch (error) {
      toast.error("Failed to delete topic");
    } finally {
      setDeletingId(null);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="w-full py-6 px-8 flex items-center justify-between border-b border-border/50">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          className="flex items-center gap-3 cursor-pointer"
          onClick={() => navigate("/")}
        >
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-indigo-600 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold tracking-tight">ConceptKit</span>
        </motion.div>

        <div className="flex items-center gap-4">
          <ModeToggle />
          <Button
            onClick={() => navigate("/")}
            className="gap-2 rounded-full shadow-sm"
            data-testid="new-topic-btn"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">New Topic</span>
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        {/* Title and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <h1 className="text-3xl sm:text-4xl font-bold mb-2">Your Library</h1>
          <p className="text-muted-foreground mb-6">
            {topics.length} saved {topics.length === 1 ? "topic" : "topics"}
          </p>

          <div className="relative max-w-md">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search topics..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 h-12 bg-background border-border rounded-xl focus:border-primary/50"
              data-testid="library-search"
            />
          </div>
        </motion.div>

        {/* Topics Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-cyan-500" />
          </div>
        ) : filteredTopics.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-20"
          >
            <FolderOpen className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">
              {searchQuery ? "No matching topics" : "Your library is empty"}
            </h2>
            <p className="text-muted-foreground mb-6">
              {searchQuery
                ? "Try a different search term"
                : "Start by understanding a new topic"}
            </p>
            {!searchQuery && (
              <Button
                onClick={() => navigate("/")}
                className="rounded-full bg-gradient-to-r from-cyan-500 to-indigo-600"
                data-testid="empty-state-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create your first topic
              </Button>
            )}
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filteredTopics.map((topic, index) => (
              <motion.div
                key={topic.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card
                  className="card-interactive group cursor-pointer h-full"
                  onClick={() => navigate(`/topic/${topic.id}`)}
                  data-testid={`topic-card-${index}`}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <Badge
                        variant="outline"
                        className="text-xs text-primary border-primary/30"
                      >
                        {topic.classifier?.topic_type || "Concept"}
                      </Badge>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`delete-topic-${index}`}
                          >
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent className="glass border-border/50">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete topic?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will permanently remove this topic from your library.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="bg-muted border-border">
                              Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                              variant="destructive"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(topic.id);
                              }}
                              className="rounded-full"
                              data-testid="confirm-delete-btn"
                            >
                              {deletingId === topic.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                "Delete"
                              )}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                    <CardTitle className="text-lg leading-tight mt-2 group-hover:text-cyan-400 transition-colors">
                      {topic.summary || "Untitled Topic"}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                      {topic.synthesis?.what_it_is || topic.input_text?.slice(0, 150)}
                    </p>

                    {/* Tags */}
                    {topic.classifier?.tags && (
                      <div className="flex flex-wrap gap-1 mb-4">
                        {topic.classifier.tags.slice(0, 3).map((tag) => (
                          <Badge
                            key={tag}
                            variant="secondary"
                            className="text-xs bg-muted/50"
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(topic.created_at)}
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {topic.input_mode === "paste" ? "Pasted" : "Asked"}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </main>
    </div >
  );
}
